'use client';
import {
  Box,
  ButtonBase,
  ButtonProps,
  darken,
  getContrastRatio,
  lighten,
  Typography,
  useTheme,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import Optional from '@jd/ui/components/Optional';

const buttonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'uppercase',
  px: 3,
  py: 1,
  borderRadius: 1,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
};

const animationBoxSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
};

const AnimatedArrowButton = ({ children, loading, ...rest }: ButtonProps) => {
  const theme = useTheme();
  const sx = buttonSx;
  const { loadingPosition, ...baseButtonProps } = rest;

  const contrastToDark = getContrastRatio(theme.palette.primary.main, '#000');
  const bgFn = contrastToDark > 2 ? darken : lighten;
  return (
    <ButtonBase
      {...baseButtonProps}
      sx={{
        ':hover div, :focus div': {
          backgroundColor: bgFn(theme.palette.primary.main, 0.1),
        },
        span: {
          display: 'flex',
          transition: 'transform 0.4s cubic-bezier(0.76, 0, 0.24, 1);',
        },
        ':hover > div:first-of-type span': {
          transform: 'translateY(-100%)',
        },

        ':hover > div:last-of-type span': {
          transform: 'translateX(100%)',
        },

        ':hover': {
          textDecoration: 'none !important',
        },
        ...baseButtonProps.sx,
      }}
    >
      <Box sx={{ ...sx, fontSize: 15, lineHeight: 1.75, whiteSpace: 'nowrap' }}>
        <Box sx={animationBoxSx}>
          <Typography variant={'button'} component="span">
            {children}
          </Typography>
          <Typography
            variant={'button'}
            component="span"
            aria-hidden
            sx={{ position: 'absolute', top: '100%' }}
          >
            {children}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ...sx,
          p: 0,
          width: '42px',
          height: '42px',
          minWidth: '42px',
        }}
      >
        <Box sx={animationBoxSx}>
          <Optional condition={!!loading}>
            <CircularProgress
              size={24}
              sx={{ color: 'primary.contrastText' }}
            />
          </Optional>
          <Optional condition={!loading}>
            <Box component="span">
              <ArrowForward />
            </Box>
            <Box
              component="span"
              aria-hidden
              sx={{ position: 'absolute', right: '100%' }}
            >
              <ArrowForward />
            </Box>
          </Optional>
        </Box>
      </Box>
    </ButtonBase>
  );
};

export default AnimatedArrowButton;
